/**
 * Direct-OIDC auth adapter: a PKCE public client against the customer IDP via oidc-client-ts.
 */

import { UserManager, WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts"
import type { AuthEventHandlers, AuthStrategy, UserInfo } from "./auth"
import { getDirectOidcConfig } from "../config"

/**
 * Read a claim by path. Try the literal key first so dotted keys like Auth0's
 * `https://app.example.com/roles` work, then descend by dots (e.g. Keycloak's
 * `realm_access.roles`). Mirrors the backend authorizer's `getClaimByPath`.
 */
function getClaimByPath(claims: Record<string, unknown>, path: string): unknown {
  if (path in claims) return claims[path]

  return path.split(".").reduce<unknown>((current, segment) => {
    if (current !== null && typeof current === "object") {
      return (current as Record<string, unknown>)[segment]
    }
    return undefined
  }, claims)
}

/**
 * Map IDP token claims to CDR's user shape using the configured identifier and email claims. Throw
 * when the identifier claim is missing or not a string; the API authorizer rejects such a token
 * anyway.
 */
export function mapClaimsToUserInfo(claims: Record<string, unknown>): UserInfo {
  const config = getDirectOidcConfig()
  const identifier = getClaimByPath(claims, config.claimIdentifier)
  if (typeof identifier !== "string" || !identifier) {
    throw new Error(
      `IDP token is missing the required identifier claim "${config.claimIdentifier}"`,
    )
  }
  const principalId = identifier

  let email: string | undefined
  for (const claimName of config.claimEmail) {
    const value = getClaimByPath(claims, claimName)
    if (typeof value === "string" && value) {
      email = value
      break
    }
  }

  return { principalId, email, isAuthenticated: true }
}

export class DirectOidcAuthStrategy implements AuthStrategy {
  private _userManager: UserManager | null = null

  /**
   * Build the UserManager lazily from runtime config. Keep the user in localStorage so sessions
   * survive new tabs and reloads, and in-flight PKCE state in sessionStorage.
   *
   * Renewal uses only the refresh_token grant (iframe renewal breaks under third-party-cookie
   * blocking), so the deployment must request `offline_access` or the session ends at token
   * expiry.
   */
  private get userManager(): UserManager {
    if (this._userManager) return this._userManager
    const config = getDirectOidcConfig()
    const settings: UserManagerSettings = {
      authority: config.oidcIssuerUrl,
      client_id: config.oidcClientId,
      redirect_uri: config.oidcRedirectUri,
      post_logout_redirect_uri: config.oidcPostLogoutRedirectUri,
      scope: config.oidcScopes.join(" "),
      response_type: "code",
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    }
    this._userManager = new UserManager(settings)
    return this._userManager
  }

  async login(_username: string, _password: string): Promise<UserInfo> {
    throw new Error("Password login is not supported in direct-oidc mode")
  }

  async loginWithRedirect(): Promise<void> {
    await this.userManager.signinRedirect()
  }

  async completeRedirectCallback(): Promise<UserInfo | null> {
    let user
    try {
      user = await this.userManager.signinRedirectCallback()
    } catch (error) {
      // Log the OIDC error; show the user only a generic message.
      console.error("Direct-OIDC callback exchange failed:", error)
      throw new Error("Sign-in did not complete. Please try again.")
    }
    if (!user || user.expired) {
      throw new Error("Failed to get user information after sign-in.")
    }
    return mapClaimsToUserInfo(user.profile as Record<string, unknown>)
  }

  async getCurrentUserInfo(): Promise<UserInfo | null> {
    const user = await this.userManager.getUser()
    if (!user || user.expired) return null
    return mapClaimsToUserInfo(user.profile as Record<string, unknown>)
  }

  async getApiToken(): Promise<string> {
    const user = await this.userManager.getUser()
    return user?.access_token ?? ""
  }

  async logout(): Promise<void> {
    await this.userManager.removeUser()
  }

  subscribe(handlers: AuthEventHandlers): () => void {
    const onLost = () => handlers.onSessionLost()
    const events = this.userManager.events
    events.addAccessTokenExpired(onLost)
    events.addSilentRenewError(onLost)
    return () => {
      events.removeAccessTokenExpired(onLost)
      events.removeSilentRenewError(onLost)
    }
  }
}
